import { useState, useCallback, useEffect } from 'react';
import useApi from 'api';
import moment from 'moment';
import { CarRepairReason } from 'global/const';
import { compareObjects, getSearchDates } from 'lib';
import { useDataContext } from 'store';

export const useCarService = (options = {}) => {
    const { init } = options;
    const [cars, setCars] = useState([]);
    const getCarsReq = useApi('getCars');
    const getCarBasicReq = useApi('getCarBasic');
    const getCarDataReq = useApi('getCarData');
    const getCarStatusReq = useApi('getCarStatus');
    const getCarDeviceStatusReq = useApi('getCarDeviceStatus');
    const patchCarReq = useApi('patchCar');
    const getCarPhotoReq = useApi('getCarPhoto');
    const postCarPhotoReq = useApi('postCarPhoto');
    const postCarBusinessStatusReq = useApi('postCarBusinessStatus');
    const postCarCommandLockReq = useApi('postCarCommandLock');
    const postCarCommandUnlockReq = useApi('postCarCommandUnlock');
    const postCarCommandTurnOnReq = useApi('postCarCommandTurnOn');
    const postCarCommandTurnOffReq = useApi('postCarCommandTurnOff');
    const postCarCommandWakeUpReq = useApi('postCarCommandWakeUp');
    const postCarCommandWhereReq = useApi('postCarCommandWhere');
    const { init: dataOptionsInit, setCars: setDataOptions } = useDataContext();

    const getCars = useCallback(() => (
        getCarsReq({ loading: false }).then((res) => {
            if (res.carVos) {
                const data = res.carVos.map((item) => ({
                    ...item,
                    gisX: item.gisX ? item.gisX / 1000000 : '',
                    gisY: item.gisY ? item.gisY / 1000000 : '',
                    originalStationId: item.stationId,
                    originalBusinessStatus: item.businessStatus,
                    originalTaskStatus: item.taskStatus
                }), []);
                setCars(data);
                /* data options */
                const dataOptions = data
                    .map((item) => ({
                        ...item,
                        value: item.carId,
                        label: item.plateNumber
                    }))
                    .sort(compareObjects('label'));
                setDataOptions(dataOptions);
                dataOptionsInit.current.cars = true;
                return data;
            }
            return res;
        })
    ), [getCarsReq]);

    useEffect(() => {
        if (init) {
            getCars();
        }
    }, []);

    const getCar = useCallback(async (id) => {
        const res = await Promise.all([
            getCarBasicReq({ path: { id } }),
            getCarDataReq({ path: { id } }).then((item) => ({
                ...item,
                obdFuelLiter: typeof item.obdFuelLiter === 'number' ?
                    `${item.obdFuelLiter} L ${typeof item.obdFuelLiterPercentage === 'number' ?
                        ` ( ${Math.round(item.obdFuelLiterPercentage * 100)} % )` : ''}` : '',
                obdTirePressure:
                    item.obdTirePressure1 || item.obdTirePressure2 ||
                    item.obdTirePressure3 || item.obdTirePressure4 ? `
                        ${item.obdTirePressure1 || 0} PSI, ${item.obdTirePressure2 || 0} PSI,
                        ${item.obdTirePressure3 || 0} PSI, ${item.obdTirePressure4 || 0} PSI
                    ` : '',
                troubleCodes: item.troubleCodes?.replace(/,|，/g, '\n'),
                gpsTime: item.gpsTime ? moment(item.gpsTime).format('YYYY/MM/DD HH:mm:ss') : '',
                obdTime: item.obdTime ? moment(item.obdTime).format('YYYY/MM/DD HH:mm:ss') : ''
            })),
            getCarStatusReq({ path: { id } })
        ]);
        const result = ['basic', 'data', 'status']
            .reduce((prev, curr, i) => ({ ...prev, [curr]: !res[i]?.error ? (res[i] || {}) : {} }), {});
        return result;
    }, [getCarBasicReq, getCarDataReq, getCarStatusReq]);
    const getCarDeviceStatus = useCallback((id) => (
        getCarDeviceStatusReq({ path: { id }, loading: false })
    ), []);
    const patchCar = useCallback(({ id, body }) => (
        patchCarReq({ path: { id }, body })
    ), [patchCarReq]);
    const getCarPhoto = useCallback((id, carPhotoTypes) => {
        if (typeof carPhotoTypes === 'string') carPhotoTypes = [carPhotoTypes];
        return getCarPhotoReq({ path: { id }, query: { carPhotoTypes: carPhotoTypes?.join(',') }, handleError: false });
    }, [getCarPhotoReq]);
    const postCarPhoto = useCallback(({ id, body }) => (
        postCarPhotoReq({ path: { id }, body })
    ), [postCarPhotoReq]);
    const emitCarCommand = useCallback(({ command, id, body }) => {
        switch (command) {
            case 'business-status': return postCarBusinessStatusReq({ path: { id }, body: JSON.stringify(body) });
            case 'lock': return postCarCommandLockReq({ path: { id } });
            case 'unlock': return postCarCommandUnlockReq({ path: { id } });
            case 'turn-on': return postCarCommandTurnOnReq({ path: { id } });
            case 'turn-off': return postCarCommandTurnOffReq({ path: { id } });
            case 'wake-up': return postCarCommandWakeUpReq({ path: { id } });
            case 'where': return postCarCommandWhereReq({ path: { id } });
            default: return { error: true };
        }
    }, [postCarBusinessStatusReq, postCarCommandLockReq, postCarCommandUnlockReq,
        postCarCommandTurnOnReq, postCarCommandTurnOffReq, postCarCommandWakeUpReq, postCarCommandWhereReq]);

    return {
        cars,
        setCars,
        getCars,
        getCar,
        getCarDeviceStatus,
        patchCar,
        getCarPhoto,
        postCarPhoto,
        emitCarCommand
    };
};

export const useCarLogService = (options = {}) => {
    const { init, carId, date } = options;
    const [carLogs, setCarLogs] = useState([]);
    const getCarLogsReq = useApi('getCarLogs');

    const getCarLogs = useCallback(async () => {
        setCarLogs([]);
        if (!carId || !date) {
            return;
        }
        let data = [];
        const dates = getSearchDates([date, date]);
        for (let i = 0; i < dates.length; i += 1) {
            const [startTime, endTime] = dates[i];
            const currList = await getCarLogsReq({
                path: { id: carId },
                query: { startTime, endTime },
                loading: false
            }).then((res) => res.carLogOBDVos ?? []);
            data.push(...currList);
        }
        const copyData = data.map((item) => ({ ...item }));
        data = data
            .map((_, index) => {
                if (copyData[index + 1]) {
                    Object.keys(copyData[index + 1]).forEach((key) => {
                        if (!copyData[index + 1][key] && copyData[index + 1][key] !== 0) {
                            copyData[index + 1][key] = copyData[index][key];
                        }
                    });
                }
                const item = copyData[index];
                return {
                    ...item,
                    dtime: item.dtime ? moment(item.dtime).format('YYYY/MM/DD HH:mm:ss') : '',
                    gisX: item.gisX ? item.gisX / 1000000 : '',
                    gisY: item.gisY ? item.gisY / 1000000 : '',
                    obdFuelLiter: typeof item.obdFuelLiter === 'number' ?
                        `${item.obdFuelLiter} L ${typeof item.obdFuelLiterPercentage === 'number' ?
                            ` ( ${Math.round(item.obdFuelLiterPercentage * 100)} % )` : ''}` : '',
                    obdTirePressure:
                        item.obdTirePressure1 || item.obdTirePressure2 ||
                        item.obdTirePressure3 || item.obdTirePressure4 ? `
                            ${item.obdTirePressure1 || 0} PSI, ${item.obdTirePressure2 || 0} PSI,
                            ${item.obdTirePressure3 || 0} PSI, ${item.obdTirePressure4 || 0} PSI
                        ` : ''
                };
            })
            .sort(compareObjects('dtime', 'desc'));
        setCarLogs(data);
    }, [getCarLogsReq, carId, date]);

    useEffect(() => {
        if (init) {
            getCarLogs();
        }
    }, [date]);

    return {
        carLogs,
        setCarLogs,
        getCarLogs
    };
};

export const useCarTroubleCodeService = (options = {}) => {
    const { init, carId, date } = options;
    const [troubleCodes, setTroubleCodes] = useState([]);
    const getCarTroubleCodesReq = useApi('getCarTroubleCodes');

    const getTroubleCodes = useCallback(async () => {
        setTroubleCodes([]);
        if (!carId || date?.length !== 2) {
            return;
        }
        let data = [];
        const dates = getSearchDates(date);
        for (let i = 0; i < dates.length; i += 1) {
            const [startTime, endTime] = dates[i];
            const currList = await getCarTroubleCodesReq({
                path: { id: carId },
                query: { startTime, endTime },
                loading: false
            }).then((res) => res.carTroubleCodeVos ?? []);
            data.push(...currList);
        }
        data = data
            .map((item) => ({
                ...item,
                dtime: moment(item.dtime).format('YYYY/MM/DD HH:mm:ss')
            }))
            .sort(compareObjects('dtime', 'desc'));
        setTroubleCodes(data);
    }, [getCarTroubleCodesReq, carId, date]);

    useEffect(() => {
        if (init) {
            getTroubleCodes();
        }
    }, [date]);

    return {
        troubleCodes,
        setTroubleCodes,
        getTroubleCodes
    };
};

export const useCarRepairService = (options = {}) => {
    const { init } = options;
    const [carRepairs, setCarRepairs] = useState([]);
    const getCarRepairsReq = useApi('getCarRepairs');
    const postCarRepairReq = useApi('postCarRepair');
    const patchCarRepairReq = useApi('patchCarRepair');
    const patchCarRepairCancelReq = useApi('patchCarRepairCancel');

    const getCarRepairs = useCallback(() => {
        // setCarRepairs([]);
        getCarRepairsReq({ loading: false }).then((res) => {
            if (res.repairVos) {
                const data = res.repairVos
                    .map((item) => ({
                        ...item,
                        originalRepairStatus: item.repairStatus,
                        reasons1: Object.keys(CarRepairReason[0]).filter((key) => item.reasons?.includes(key)),
                        reasons2: Object.keys(CarRepairReason[1]).filter((key) => item.reasons?.includes(key)),
                        reasons3: Object.keys(CarRepairReason[2]).filter((key) => item.reasons?.includes(key))
                    }))
                    .sort(compareObjects('inProgressTime', 'desc'));
                setCarRepairs(data);
            }
        });
    }, [getCarRepairsReq]);

    useEffect(() => {
        if (init) {
            getCarRepairs();
        }
    }, []);

    const postCarRepair = useCallback((body) => (
        postCarRepairReq({ body })
    ), [postCarRepairReq]);
    const patchCarRepair = useCallback(({ id, body }) => (
        patchCarRepairReq({ path: { id }, body })
    ), [patchCarRepairReq]);
    const patchCarRepairCancel = useCallback((id) => (
        patchCarRepairCancelReq({ path: { id } })
    ), [patchCarRepairCancelReq]);

    return {
        carRepairs,
        setCarRepairs,
        getCarRepairs,
        postCarRepair,
        patchCarRepair,
        patchCarRepairCancel
    };
};

export const useStayTimeService = (options = {}) => {
    const { init } = options;
    const [stayTime, setStayTime] = useState(0);
    const getStayTimeReq = useApi('getStayTime');

    const getStayTime = useCallback(() => {
        getStayTimeReq({ loading: false }).then((res) => {
            if (res.stayTime) {
                setStayTime(res.stayTime);
            }
        });
    }, [getStayTimeReq]);

    useEffect(() => {
        if (init) {
            getStayTime();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        stayTime
    };
}